import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  FaFilter,
  FaColumns,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { GoPencil } from "react-icons/go";
import { BiHide, BiSolidBulb } from "react-icons/bi";
import ModifyColumns from "../Tools/ModifyColumns";
import {
  AddTableFilterComponent,
  apiRequestBodyBuilder,
  getSortedDataGLOBAL,
} from "../config/tableHelper";
import { RenderTable } from "../config/tableWrapper";
import { GENDER_ADDTABLE_COLUMNS } from "../Components/AddTableCols";

// Skeleton styled identically to PaidAdsPage TableSkeleton
function TableSkeleton() {
  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="flex items-center gap-8 sm:gap-12">
          <div className="h-4 w-16 bg-gray-200 rounded hidden sm:block" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded hidden md:block" />
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100 animate-pulse">
        {[1, 2, 3, 4].map((item) => (
          <div key={item} className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div
                className="h-4 bg-gray-200 rounded"
                style={{ width: `${140 + (item % 3) * 35}px` }}
              />
            </div>
            <div className="flex items-center gap-8 sm:gap-12">
              <div className="h-4 w-14 bg-gray-100 rounded hidden sm:block" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-20 bg-gray-100 rounded hidden md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Insights Skeleton styled to match the gender card layout
function InsightsSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm w-full p-4 sm:p-5 space-y-4 animate-pulse">
      {[1, 2, 3].map((item) => (
        <div
          key={item}
          className="flex flex-col sm:flex-row items-start gap-4 pb-4 border-b border-gray-100 last:border-b-0 last:pb-0"
        >
          <div className="w-12 h-12 rounded-xl bg-gray-200 shrink-0" />
          <div className="w-36 shrink-0 pt-2">
            <div className="h-4 bg-gray-200 rounded w-24" />
          </div>
          <div className="flex-grow pt-2 w-full space-y-2">
            <div className="h-3.5 bg-gray-200 rounded w-full" />
            <div
              className="h-3 bg-gray-100 rounded"
              style={{ width: `${65 + (item % 3) * 15}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// Helper function to return icon, badge styling, and title color according to gender entity type
const getGenderStyle = (entityName = "") => {
  const name = entityName.toLowerCase();
  if (name.includes("male") && !name.includes("female")) {
    return {
      bgIcon: "bg-blue-50 text-blue-600 border border-blue-100",
      titleColor: "text-blue-600",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          {/* Male Symbol */}
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10 19a5 5 0 100-10 5 5 0 000 10z M13.5 10.5L20 4 M15 4h5v5"
          />
        </svg>
      ),
    };
  } else if (name.includes("female")) {
    return {
      bgIcon: "bg-pink-50 text-pink-600 border border-pink-100",
      titleColor: "text-pink-600",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          {/* Female Symbol */}
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 14a5 5 0 100-10 5 5 0 000 10z M12 14v7 M9 18h6"
          />
        </svg>
      ),
    };
  } else {
    return {
      bgIcon: "bg-indigo-50 text-indigo-600 border border-indigo-100",
      titleColor: "text-indigo-600",
      icon: (
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          {/* Generic/Unspecified Symbol */}
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
    };
  }
};

const GenderInfo = ({
  startDate,
  endDate,
  initialDateRange,
  insightAvailable = false,
  PageType,
}) => {
  const [unfilteredData, setUnfilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState(GENDER_ADDTABLE_COLUMNS);
  const [groups, setGroups] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [campaignNameInput, setCampaignNameInput] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState(null);

  // Editable Insights State
  const [insightsArray, setInsightsArray] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [isEditingInsight, setIsEditingInsight] = useState(false);
  const [editedInsights, setEditedInsights] = useState([]);
  const [currentGroupType, setCurrencyGroupType] = useState("");

  const columnRef = useRef(null);
  const filterRef = useRef(null);

  const fetchData = async (nameContains = "") => {
    setLoading(true);
    setUnfilteredData([]);
    try {
      let requestBody = apiRequestBodyBuilder(initialDateRange, {
        reporting_type: PageType,
      });
      if (nameContains) requestBody.name_contains = nameContains;
      const endpoint = nameContains
        ? "https://api.confidanto.com/gender/filter"
        : "https://api.confidanto.com/gender/data";
      const response = await axios.post(endpoint, requestBody);
      const rawData = response.data.gender_info || response.data || [];
      if (Array.isArray(rawData)) {
        setUnfilteredData(rawData);
        setColumns(GENDER_ADDTABLE_COLUMNS);
      } else {
        setUnfilteredData([]);
      }
    } catch (error) {
      setUnfilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      let requestBody = apiRequestBodyBuilder(initialDateRange);
      const responseInfo = await fetch(
        "https://api.confidanto.com/campaign-group2/group-info",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      const groupType = await responseInfo.json();
      setCurrencyGroupType(
        groupType?.type
          ? groupType.type.charAt(0).toUpperCase() + groupType.type.slice(1)
          : "Campaign",
      );
      const groupResponse = await fetch(
        "https://api.confidanto.com/campaign-group/fetch-groups",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...requestBody,
            type: groupType?.type || "campaign",
          }),
        },
      );
      const data = await groupResponse.json();
      setGroups(data.groups || []);
    } catch (err) {
      setGroups([]);
    }
  };

  const handleApplyCampaignFilter = () => {
    fetchData(campaignNameInput);
    setShowFilterDropdown(false);
    setSelectedFilter(null);
    setAppliedFilters([
      {
        id: Date.now(),
        type: "campaign",
        value: campaignNameInput,
        label: `Campaign: ${campaignNameInput}`,
      },
    ]);
  };

  const handleGroupApply = async (remainingGroups = null) => {
    setLoading(true);
    setUnfilteredData([]);
    setShowFilterDropdown(false);
    setSelectedFilter(null);
    const activeGroups =
      remainingGroups !== null ? remainingGroups : selectedGroups;
    const selectedGroupIds = groups
      .filter((g) => activeGroups.includes(g.group_name))
      .map((g) => g.group_id);
    if (activeGroups.length === 0) {
      alert("Please select at least one group.");
      setLoading(false);
      return;
    }
    setAppliedFilters(
      activeGroups.map((group) => ({
        id: group,
        type: "groupwise",
        value: group,
        label: group,
      })),
    );
    try {
      let requestBody = apiRequestBodyBuilder(initialDateRange, {
        group_ids: selectedGroupIds,
      });
      const response = await axios.post(
        "https://api.confidanto.com/gender-group/datewise-data",
        requestBody,
      );
      setUnfilteredData(response.data);
      setColumns(
        GENDER_ADDTABLE_COLUMNS.map((col) =>
          col.key === "gender"
            ? { ...col, title: `${currentGroupType} Group`, key: "group_name" }
            : col,
        ),
      );
    } catch (error) {
      setUnfilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyStaticFilter = (filter) => {
    setAppliedFilters((prev) => [...prev, filter]);
    setShowFilterDropdown(false);
  };

  const handleRemoveFilter = async (filterToRemove) => {
    const newFilters = appliedFilters.filter((f) => f.id !== filterToRemove.id);
    if (filterToRemove.type === "groupwise") {
      const remainingGroups = newFilters
        .filter((f) => f.type === "groupwise")
        .map((f) => f.value);
      setSelectedGroups(remainingGroups);
      if (remainingGroups.length === 0) {
        setAppliedFilters([]);
        fetchData();
      } else {
        handleGroupApply(remainingGroups);
      }
    } else {
      setAppliedFilters(newFilters);
      if (filterToRemove.type === "campaign" && newFilters.length === 0)
        fetchData();
    }
  };

  const getFilteredData = () => {
    const staticFilters = appliedFilters.filter((f) => f.type === "static");
    if (staticFilters.length === 0) return unfilteredData;
    return unfilteredData.filter((row) => {
      return staticFilters.every((filter) => {
        const rowValue =
          typeof row[filter.key] === "object"
            ? row[filter.key].value
            : row[filter.key];
        if (rowValue === null || rowValue === undefined) return false;
        if (filter.keyType === "number") {
          const rowNum = parseFloat(rowValue);
          const filterNum = parseFloat(filter.value);
          if (isNaN(rowNum) || isNaN(filterNum)) return false;
          switch (filter.comparison) {
            case ">":
              return rowNum > filterNum;
            case "<":
              return rowNum < filterNum;
            case "=":
              return rowNum === filterNum;
            default:
              return true;
          }
        } else if (filter.keyType === "boolean") {
          return filter.value.includes(String(rowValue));
        } else {
          return rowValue
            .toString()
            .toLowerCase()
            .includes(filter.value.toLowerCase());
        }
      });
    });
  };

  // Insights Logic
  const fetchInsights = async () => {
    setInsightsLoading(true);
    setInsightsArray([]);

    let requestBody = apiRequestBodyBuilder(initialDateRange, {
      insight_type: "gender",
    });

    axios
      .post(
        "https://api.confidanto.com/get-insights/custom-reporting-insights",
        requestBody,
      )
      .then((res) => {
        setInsightsArray(res.data);
      })
      .catch((err) => console.error("Error Insights Gender: ", err))
      .finally(() => {
        setInsightsLoading(false);
      });
  };

  const updateInsight = async (newInsights) => {
    try {
      const baseRequestInfo = apiRequestBodyBuilder(initialDateRange, {});

      const updatePromises = newInsights.map((insight) => {
        const requestBody = {
          id: insight.id,
          ad_account_id: insight.ad_account_id || baseRequestInfo.ad_account_id,
          insight_type: insight.insight_type || "gender",
          entity_name: insight.entity_name,
          insight_text: insight.insight_text,
        };

        return axios.post(
          "https://api.confidanto.com/get-insights/update-custom-reporting-insights",
          requestBody,
        );
      });

      await Promise.all(updatePromises);
    } catch (error) {
      console.error("Error updating insights: ", error);
      alert("Failed to save some insights. Please try again.");
    }
  };

  const handleToggleInsights = () => {
    if (!showInsights && insightsArray.length === 0) {
      fetchInsights();
    }
    const newShowState = !showInsights;
    setShowInsights(newShowState);

    try {
      const currentStored = JSON.parse(
        localStorage.getItem("customrep_insight") || "{}",
      );
      currentStored["gender"] = newShowState;
      localStorage.setItem("customrep_insight", JSON.stringify(currentStored));
    } catch (e) {
      console.error("Failed to save insight state", e);
    }
  };

  const handleEditClick = () => {
    setEditedInsights(insightsArray.map((insight) => ({ ...insight })));
    setIsEditingInsight(true);
  };

  const handleInsightChange = (index, newValue) => {
    const newArr = [...editedInsights];
    newArr[index] = { ...newArr[index], insight_text: newValue };
    setEditedInsights(newArr);
  };

  const handleSaveInsight = async () => {
    setInsightsLoading(true);
    await updateInsight(editedInsights);
    setInsightsArray(editedInsights);
    setIsEditingInsight(false);
    setInsightsLoading(false);
  };

  useEffect(() => {
    fetchGroups();
    fetchData();
    setAppliedFilters([]);
    setInsightsArray([]);
    setShowInsights(false);
    setIsEditingInsight(false);

    try {
      const currentStored = JSON.parse(
        localStorage.getItem("customrep_insight") || "{}",
      );
      currentStored["gender"] = false;
      localStorage.setItem("customrep_insight", JSON.stringify(currentStored));
    } catch (e) {
      console.error(e);
    }
  }, [initialDateRange]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideMuiMenu = event.target.closest(
        ".groupwise-select-menu",
      );
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target) &&
        !isClickInsideMuiMenu
      ) {
        setShowFilterDropdown(false);
        setSelectedFilter(null);
      }
      if (columnRef.current && !columnRef.current.contains(event.target))
        setShowColumnsMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredData = getFilteredData();
  const activeInsightsData = isEditingInsight ? editedInsights : insightsArray;

  return (
    <div className="flex h-full bg-gray-100 font-roboto">
      <main className="w-full p-4">
        <div className="flex justify-between items-center mb-3 space-x-1.5">
          <div
            className={`transition-opacity duration-300 ${
              loading ? "pointer-events-none opacity-50" : ""
            }`}
          >
            <AddTableFilterComponent
              data={unfilteredData}
              filterRef={filterRef}
              showFilterDropdown={showFilterDropdown}
              setShowFilterDropdown={setShowFilterDropdown}
              appliedFilters={appliedFilters}
              handleRemoveFilter={handleRemoveFilter}
              selectedFilter={selectedFilter}
              setSelectedFilter={setSelectedFilter}
              columns={columns}
              campaignNameInput={campaignNameInput}
              setCampaignNameInput={setCampaignNameInput}
              onApplyStaticFilter={handleApplyStaticFilter}
              onFetchGroups={fetchGroups}
              onApplyCampaignFilter={handleApplyCampaignFilter}
              groups={groups}
              selectedGroups={selectedGroups}
              setSelectedGroups={setSelectedGroups}
              onApplyGroupFilter={handleGroupApply}
              currentGroupType={currentGroupType}
            />
          </div>
          <div className="flex space-x-1.5">
            <div className="relative" ref={columnRef}>
              <button
                disabled={loading}
                className={`bg-transparent text-gray-600 px-3 py-1.5 rounded flex flex-col items-center justify-center transition-all ${
                  loading
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-slate-100"
                }`}
                onClick={() => setShowColumnsMenu(!showColumnsMenu)}
              >
                <FaColumns className="text-sm" />
                <span className="text-sm">Columns</span>
              </button>
              {showColumnsMenu && (
                <ModifyColumns
                  columns={columns}
                  setColumns={setColumns}
                  setShowColumnsMenu={setShowColumnsMenu}
                  colsLimit={13}
                />
              )}
            </div>
          </div>
        </div>

        {/* Table & Skeleton Loading */}
        {loading ? (
          <div className="mt-3">
            <TableSkeleton />
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded shadow mt-3">
            {filteredData.length > 0 ? (
              <RenderTable
                columns={columns}
                data={filteredData}
                enableExpandCollapse={initialDateRange?.length > 1}
              />
            ) : (
              <div className="flex justify-center items-center h-40 mt-6">
                <h2>No Data Found</h2>
              </div>
            )}
          </div>
        )}

        {insightAvailable && (
          <div className="w-full my-3">
            <button
              disabled={loading}
              onClick={handleToggleInsights}
              className={`flex items-center justify-center gap-1.5 w-auto bg-[#283593] text-white font-semibold py-1.5 px-3 rounded-lg shadow-md transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-opacity-75 ${
                loading ? "opacity-50 cursor-not-allowed" : "hover:bg-[#1a237e]"
              }`}
            >
              {showInsights ? (
                <>
                  <BiHide size="1.2em" />
                  <span className="text-sm">Hide Insights</span>
                </>
              ) : (
                <>
                  <BiSolidBulb size="1.2em" />
                  <span className="text-sm">Show Insights</span>
                </>
              )}
            </button>

            <div
              className={`w-full transition-all duration-500 ease-in-out overflow-hidden ${
                showInsights ? " opacity-100 mt-2" : "max-h-0 opacity-0"
              }`}
            >
              {insightsLoading ? (
                <InsightsSkeleton />
              ) : (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm w-full relative overflow-hidden">
                  {!isEditingInsight && insightsArray.length > 0 && (
                    <button
                      onClick={handleEditClick}
                      className="absolute top-4 right-4 bg-blue-600 text-white p-2 rounded-full shadow hover:bg-blue-700 transition-transform hover:scale-105 z-10"
                      title="Edit Insights"
                    >
                      <GoPencil size={16} />
                    </button>
                  )}

                  {insightsArray.length > 0 ? (
                    <div className="divide-y divide-gray-100">
                      {activeInsightsData.map((item, index) => {
                        const style = getGenderStyle(item.entity_name);
                        return (
                          <div
                            key={index}
                            className="p-4 sm:p-5 flex flex-col sm:flex-row items-start gap-4 hover:bg-slate-50/50 transition-colors"
                          >
                            {/* Gender Icon Box */}
                            <div
                              className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${style.bgIcon}`}
                            >
                              {style.icon}
                            </div>

                            {/* Gender Title */}
                            <div
                              className={`w-36 shrink-0 font-bold text-base pt-2.5 sm:pt-2 ${style.titleColor}`}
                            >
                              {item.entity_name}
                            </div>

                            {/* Gender Text Summary or Textarea in edit mode */}
                            <div className="flex-grow text-xs sm:text-sm text-slate-600 leading-relaxed pt-2.5 sm:pt-2 w-full">
                              {isEditingInsight ? (
                                <div className="grid w-full">
                                  <textarea
                                    className="col-start-1 col-end-2 row-start-1 row-end-2 w-full resize-none overflow-hidden bg-white border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-md p-2 outline-none transition-all duration-200 text-slate-700 leading-relaxed"
                                    value={item.insight_text}
                                    onChange={(e) =>
                                      handleInsightChange(index, e.target.value)
                                    }
                                    rows={3}
                                  />
                                </div>
                              ) : (
                                <span>{item.insight_text}</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-sm text-red-500">
                      No Insights found for above data
                    </div>
                  )}

                  {isEditingInsight && (
                    <div className="flex justify-end gap-3 p-4 bg-slate-50 border-t border-gray-100">
                      <button
                        onClick={() => setIsEditingInsight(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveInsight}
                        className="px-4 py-2 text-sm font-medium text-white bg-[#283593] border border-transparent rounded-lg shadow-sm hover:bg-[#1a237e] focus:outline-none transition-all"
                      >
                        Save Changes
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default GenderInfo;
